<template>
  <div class="dashboard-chart-wrapper">
    <svg ref="lineChart"></svg>
    <div ref="tooltip" class="tooltip-wrapper" style="display: none;"></div>
  </div>
</template>

<script>
import numeral from 'numeral';
import * as d3 from 'd3';
import moment from 'moment';
import { SIDEBAR_CALLAPSED } from '@/common/events.types';
import { SESSION_TYPES } from '@/constants/SessionConsts';
import { MONTH_DAY_FORMAT } from '@/constants/DateConstants';

export default {
  name: 'LineChart',
  props: {
    data: Array,
    height: Number,
    y: Object,
    xUnit: String,
    yUnit: String,
    yAxisTitle: String,
    xAxisTitle: String,
    color: String,
    player: Object,
    selectedTeam: Object,
    yTicks: Array,
    rightYLabelAxis: Object,
    teams: Array,
    minValue:Number,
    maxValue:Number,
    itemsPerPage:Number
  },
  mounted () {
    this.initChart();

    this.$eventHub.$on('dashboard-page-updated', () => {
      if(this.$refs.tooltip) {
        this.$refs.tooltip.style.display = "none";
      }
    });

    this.$eventHub.$on(SIDEBAR_CALLAPSED, () => {
      setTimeout(function() {
        this.initChart();
      }.bind(this));
    })

    window.addEventListener('resize', this.initChart);
  },

   beforeDestroy () {
    window.removeEventListener('resize', this.initChart);
    this.$eventHub.$off();
  },
  data () {
    return {
      animationDuration: 300,
      margin: { top: 50, right: 50, bottom: 50, left: 70 },
      xAxisSteps: 10,
      tooltipWidth: 200,
      tooltipHeight: 70,
      tooltipSelectedPoint: null
    }
  },
  
  computed: {
    locationHref () {
      return `${window.location.origin}${this.$router.currentRoute.fullPath}`;
    },
    xTicks () {
      const xTicks = [];
      for(let i = 0; i < this.data.length; i++) {
        xTicks.push(moment(this.data[i].date).toDate());
      }

      xTicks.sort((a, b) => (a - b));

      return xTicks;
    },
  },
  methods: {
    initChart () {
      if(this.svg) {
        this.svg.selectAll("*").remove();
      }

      const context = this;
      this.width = this.$refs.lineChart.parentElement.offsetWidth - this.margin.right - 80;

      if(window.innerWidth < 768) {
        this.width = this.width + 20;
      }

      this.xScale = d3
        .scaleTime()
        .domain(d3.extent(this.xTicks, function(d) {
          return d;
        }))
        .range([0, this.width]);

      this.yScale = d3
        .scaleLinear()
        .domain([this.yTicks[0], this.yTicks[this.yTicks.length - 1]])
        .range([this.height, 0]);

      this.yScaleRight = d3
        .scaleLinear()
        .domain([this.yTicks[0], this.yTicks[this.yTicks.length - 1]])
        .range([this.height, 0]);

      // line generator
      this.line = d3
        .line()
        .x(function(d) {
          return context.xScale(moment(d.date).toDate());
        })
        .y(function(d) {
          return context.yScale(d.value);
        })
        .curve(d3.curveLinear) 

      // Add the SVG to the page
      this.svg = d3
        .select(this.$refs.lineChart)
        .attr("width", this.width + this.margin.left + this.margin.right + 50)
        .attr("height", this.height + this.margin.top + this.margin.bottom)

      if(window.innerWidth > 768) {
        this.main = this.svg
          .append("g")
          .attr("transform", "translate(" + this.margin.left + "," + this.margin.top + ")");
      } else {
        this.main = this.svg
          .append("g")
          .attr("transform", "translate(" + 45 + "," + this.margin.top + ")");
      }
      

      // Add defs to chart
      this.defs = this.main.append('defs');

      this.clipPath = this.defs
        .append('clipPath')
        .attr('id', 'clip')

      this.clipPathRect = this.clipPath
        .append('rect')
        .attr('x', 0)
        .attr('y', 0)
        .attr('width', this.width)
        .attr('height', this.height);

      this.clipXaxis = this.defs
        .append('clipPath')
        .attr('id', 'clipXaxis');

      this.clipXaxisRect = this.clipXaxis
        .append('rect')
        .attr('x', -20)
        .attr('y', 0)
        .attr('width', this.width + 40)
        .attr('height', this.height + 300);

      this.clipYaxis = this.defs
        .append('clipPath')
        .attr('id', 'clipYaxis');
        
      this.clipYaxisRect = this.clipYaxis
        .append('rect')
        .attr('x', -50)
        .attr('y', -10)
        .attr('height', context.height + 40)
        .attr('width', 120);

      this.xGridPath = this.defs
        .append('clipPath')
        .attr('id', 'xGridPath');

      this.xGridPathRect = this.xGridPath.append('rect')
        .attr('x', 0)
        .attr('y', -context.height)
        .attr('height', context.height)
        .attr('width', context.width)

      // Call the x axis in a group tag
      this.xAxis = d3
        .axisBottom(this.xScale)
        .tickValues(this.xTicks)
        .tickFormat(this.xTicksDateFormat)

      this.yAxis = d3.axisLeft(this.yScale).tickValues(this.yTicks);

      this.rightYLabelAxis.array.push(this.yTicks[this.yTicks.length - 1]);
      this.rightYLabelAxis.labels[this.yTicks[this.yTicks.length - 1]] = '';
      
      this.yAxisRight = d3
        .axisRight(this.yScaleRight)
        .tickFormat(function(d) {
          return context.rightYLabelAxis.labels[d];
        })
        .tickValues(this.rightYLabelAxis.array);

      this.xAxisChartTicks = this.main
        .append("g")
        .attr("class", "x axis")
        .attr('clip-path', 'url(' + this.locationHref + '#clipXaxis)')
        .attr("transform", "translate(0," + this.height + ")")
        .call(this.xAxis);

      if(this.width < 500) {
        this.xAxisChartTicks
          .selectAll("text")	
          .style("text-anchor", "end")
          .attr("dx", "-.8em")
          .attr("dy", ".15em")
          .attr("transform", "rotate(-90)")
          .attr('class', function(d) {
            
          })
      }

      // Draw Axis
      // Call the y axis in a group tag
      this.yAxisG = this.main
        .append("g")
        .attr("class", "y axis")
        .call(this.yAxis)

      this.yAxisRightG = this.main
        .append("g")
        .attr("class", "y axis right-y-axis")
        .attr("transform", "translate( " + this.width + ", 0 )")
        .call(this.yAxisRight)

      if(window.innerWidth > 768) {
        this.yAxisLabelWrapper = this.main  
          .append("g")
          .attr("fill", "#5E5E62")

        // Y axix label
        this.yAxisLabel = this.yAxisLabelWrapper
          .append("text")
          .attr("transform", "rotate(-90)")
          .attr("class", "axis-label axis-label-y")
          .attr("x", -this.height / 2)
          .attr("y", -70)
          .attr("dy", ".71em")
          .style("text-anchor", "middle")
          .text(this.yAxisTitle); 

        // X axix label
        this.xAxisLabelWrapper = this.main
          .append("g")
          .attr("fill", "#5E5E62")

        this.xAxisLabel = this.xAxisLabelWrapper
          .append("text")
          .attr("transform", "translate(0," + this.height + ")")
          .attr("class", "axis-label axis-label-x")
          .attr("x", this.width / 2)
          .attr("y", 50)
          .style("text-anchor", "middle")
          .text(this.xAxisTitle);
      }

      this.main
        .append("g")
        .attr("class", "grid grid-x")
        .attr("transform", "translate(0, " + this.height + ")")
        .attr('clip-path', 'url(' + this.locationHref + '#xGridPath)')
        .call(
          d3
            .axisBottom(this.xScale)
            .ticks(this.xTicks.length)
            .tickValues(this.xTicks)
            .tickSize(-this.height)
            .tickFormat("")
        ); // Create an axis component with d3.axisBottom

      // Add a Y gridlines
      this.yGridlines = this.main
        .append("g")
        .attr("class", "grid grid-y")
        .call(
          d3
            .axisLeft(this.yScale)
            .ticks(this.yTicks.length)
            .tickValues(this.yTicks)
            .tickSize(-this.width)
            .tickFormat("")
        ); // Create an axis component with d3.axisBottom

      this.pathGroup = this.main
        .append('g')
        .attr('class', 'path-group');

      this.pathGroup
        .append("path")
        .attr("class", "line path main")
        .attr('clip-path', 'url(' + this.locationHref + '#clip)')
        .attr("fill", "none")
        .style("stroke", this.color || '#fff')
        .style("stroke-dasharray", 0)
        .attr("stroke-linejoin", "round")
        .attr("stroke-linecap", "round")
        .attr("stroke-width", 1)
        .data([this.data])
        .attr("d", this.line)
        .transition()

      this.yAxisG
        .attr('clip-path', 'url(' + this.locationHref + '#clipYaxis)')

      this.yGridlines
        .attr('clip-path', 'url(' + this.locationHref + '#clip)');

      this.main.selectAll(".dot")
        .data(this.data)
      .enter().append("circle")
        .attr("class", "dot")
        .attr("cx", function(d, i) {
          if(d.points && d.points.length) {
            let numberOfIndividualSessions = 0;
            let numberOfTrainingSessions = 0;
            let numberOfGameSessions = 0;
            let numberOfOtherSessions = 0;

            for(let i = 0; i < d.points.length; i++) {
              if(d.points[i].activityType === SESSION_TYPES.INDIVIDUAL) {
                numberOfIndividualSessions++;
              } else if (d.points[i].activityType === SESSION_TYPES.TRAINING) {
                numberOfTrainingSessions++;
              } else if (d.points[i].activityType === SESSION_TYPES.GAME) {
                numberOfGameSessions++;
              } else if (d.points[i].activityType === SESSION_TYPES.OTHER) {
                numberOfOtherSessions++;
              }
            }

            if(numberOfIndividualSessions === d.points.length) {
              d3.select(this).attr('class', 'circle full-gray-circle-border-white');

              return context.xScale(moment(d.date).toDate());
            }

            if(numberOfGameSessions === d.points.length) {
              d3.select(this).attr('class', 'circle full-gray-circle');

              return context.xScale(moment(d.date).toDate());
            }

            if(numberOfTrainingSessions === d.points.length) {
              d3.select(this).attr('class', 'circle border-gray-circle');

              return context.xScale(moment(d.date).toDate());
            }

            if(numberOfOtherSessions === d.points.length) {
              d3.select(this).attr('class', 'circle border-gray-circle');

              return context.xScale(moment(d.date).toDate());
            }

            if((numberOfTrainingSessions + numberOfOtherSessions) === d.points.length) {
              d3.select(this).attr('class', 'circle border-gray-circle');
      
              return context.xScale(moment(d.date).toDate());
            }

            d3.select(this).attr('class', 'circle white-circle');
            return context.xScale(moment(d.date).toDate());
          }
          
          d3.select(this).attr("stroke", context.color || '#fff');
          d3.select(this).style("fill", context.color || '#fff');
          d3.select(this).style("cursor", 'pointer');

          d3.select(this).attr("r", 2);
          d3.select(this).attr("stroke-width", 2);


          return context.xScale(moment(d.date).toDate());
        })
        .attr("cy", function(d) { 
          return context.yScale(d.value) 
        })
        .attr("r", function(d, i) {
          if(d.points && d.points.length) {
            return 5;
          }
          return 3;
        })
          .on("click", function(point, b, c) {
            if(context.tooltipSelectedPoint === point.date) {
              context.$refs.tooltip.style.display = "none";
              context.tooltipSelectedPoint = null;
              return false;
            }

            context.tooltipSelectedPoint = point.date;
            context.$refs.tooltip.innerHTML = '';
            if(point.points && point.points.length) {
              point.points.forEach(p => {
                const temp = document.createElement('div');
                temp.classList.add('info-wrapper');

                const styles = context.generatePointColor(p.activityType);
                const background = styles.background;
                const borderColor = styles.borderColor;
                const teamText = (() => {
                  if(p.activityType === SESSION_TYPES.INDIVIDUAL) {
                    return `${context.player.firstName} ${context.player.lastName}`;
                  }

                  if(p.teamId) {
                    const team = context.teams.filter(t => (t.teamId === p.teamId))[0];
                    return team.teamName;
                  } else {
                    return context.selectedTeam.teamName;
                  }
                })();

                if(p.activityId) {
                  temp.innerHTML = 
                    `<a data-activity-id=${p.activityId} class="tooltip-link link">
                      <span class="circle" style="background-color: ${background}; border: 1px solid ${borderColor};"></span>
                      ${!p.activityId ? (`<i class="fas fa-exclamation-triangle text-red margin-right-5"></i>`) : ''}
                      <span class="text">
                        ${p.playerActivityStatus !== 'PROCESSED' ? '<i class="fas fa-exclamation-triangle text-red margin-right-5"></i>' : '' }
                        ${teamText}
                        ${numeral(p.value).format('0,0')}${context.yUnit}
                      </span>
                    </a>`;
                }

                context.$refs.tooltip.appendChild(temp);
              });

              const links = document.querySelectorAll('.tooltip-link.link');
              
              links.forEach(function(elem) {
                elem.addEventListener('click', function(e) {
                  const activityId = e.currentTarget.getAttribute('data-activity-id');
                  
                  context.$router.push({ name: 'CalendarActivity', params: { activityId } })
                })
              })
              
            } else {
              const temp = document.createElement('div');
              
              temp.classList.add('info-wrapper');
              temp.innerHTML = context.$t('DASHBOARD.lineChart.noActivities');
              context.$refs.tooltip.appendChild(temp);
            }

            const leftPosition = context.xScale(moment(point.date).toDate()) + 35 + context.tooltipWidth;
            const topPositionModifier = (point.points && point.points.length) ? point.points.length * 15 : 0;

            if(window.innerWidth > 768) {
              if(leftPosition > context.width) {
                context.$refs.tooltip.style.left = (context.xScale(moment(point.date).toDate()) + 35 - context.tooltipWidth) + 'px';
                context.$refs.tooltip.style.top = context.yScale(point.value) - topPositionModifier + 'px';
                context.$refs.tooltip.style.display = "block";
              } else {
                context.$refs.tooltip.style.left = (context.xScale(moment(point.date).toDate()) + 35) + 'px';
                context.$refs.tooltip.style.top = context.yScale(point.value) - topPositionModifier + 'px';
                context.$refs.tooltip.style.display = "block";
              }
            } else {
              if(leftPosition > context.width) {
                const startLeft = context.xScale(moment(point.date).toDate()) + 35 - context.tooltipWidth;

                if(startLeft < 0) {
                  context.$refs.tooltip.style.left
                } else {
                  context.$refs.tooltip.style.left = (context.xScale(moment(point.date).toDate()) + 35 - context.tooltipWidth) + 'px';
                }
                context.$refs.tooltip.style.top = context.yScale(point.value) - topPositionModifier + 'px';
                context.$refs.tooltip.style.display = "block";
              } else {
                context.$refs.tooltip.style.left = (context.xScale(moment(point.date).toDate()) + 35) + 'px';
                context.$refs.tooltip.style.top = context.yScale(point.value) - topPositionModifier + 'px';
                context.$refs.tooltip.style.display = "block";
              }
            }
          })

      this.tooltip = this.appendAndGenerateTooltip(this.main, this.tooltipWidth, this.tooltipHeight, this.locationHref);

      d3.selectAll('.right-y-axis g.tick')
        .filter(function(d){ 
          return context.rightYLabelAxis.labels[d]
        })
        .select('line')
        .attr('class', 'tick-line-hidden');
    },
    xTicksDateFormat (date, xTick, h, f) {
       if(this.itemsPerPage===30){
          if(xTick%2===0){
             return moment(date).format(MONTH_DAY_FORMAT);
          }
          else{
             return "";
          }
       }
       else return moment(date).format(MONTH_DAY_FORMAT); 
    },
    appendAndGenerateTooltip (root, width, height) {
      const tooltip = root.append("g")
        .attr("class", "chart-tooltip")
        .style("display", "none");

      tooltip.append("rect")
        .attr("width", width)
        .attr("height", height)
        .attr("fill", "#000000");
      
      return tooltip;
    },
    generatePointColor (activityType) {
      if(activityType === SESSION_TYPES.INDIVIDUAL) {
        return {
          background: '#121214',
          borderColor: '#fff'
        };
      }

      if(activityType === SESSION_TYPES.TRAINING) {
        return {
          background: '#121214',
          borderColor: '#5E5E62'
        };
      }

      if(activityType === SESSION_TYPES.OTHER) {
        return {
          background: '#121214',
          borderColor: '#5E5E62'
        };
      }

      if(activityType === SESSION_TYPES.GAME) {
        return {
          background: '#5E5E62',
          borderColor: '#5E5E62'
        };
      }
    }
  },
  watch: {
    data () {
      this.initChart();
    }
  }
}
</script>

<style lang="scss">
  .dashboard-chart-wrapper {
    margin-bottom: 20px;
    position: relative;
    padding: 10px;
    overflow: hidden;

    .tooltip-wrapper {
      background: #000;
      position: absolute;
    }

    .axis {
      color: #5E5E62;
      font-size: 14px;
    }

    .line {
      fill: none;
      stroke: #F68D2E;
      stroke-width: 1;
    }

    .line.main {
      stroke-width: 2;
    }

    .axis-label {
      color: #fff;
      font-family: sans-serif;
      font-size: 14px;
    }

    .overlay-rect {
      fill: none;
      pointer-events: all;
    }
    
    .hover-line {
      stroke-width: 2px;
      stroke-dasharray: 3,3;
      display: none;
    }

    /* Style the dots by assigning a fill and stroke */
    .circle {
      cursor: pointer;
    }

    .grid line {
      stroke: lightgrey;
      stroke-opacity: 0.7;
      shape-rendering: crispEdges;
    }

    .grid path {
      stroke-width: 0;
    }

    .grid .tick line {
      stroke: #2f2f2f;
    }

    .overlay {
      fill: none;
      pointer-events: all;
    }

    .focus circle {
      stroke-width: 1px;
      stroke: #000000;
      stroke-opacity: .3;
    }

    .brushes {
      .brush .selection {
        fill: #5E5E62;
        stroke: #5E5E62;
        stroke-width: 1px;
        stroke-dasharray: 3px 3px;
      }
    }

    .zoom-brush {
      .selection {
        stroke: $elephant-gray;
      }

      &:hover {
        .selection {
          stroke: $elephant-gray;
        }

        .handle--custom {
          stroke: $elephant-gray;
        }
      }
    } 

    .tooltip-wrapper {
      position: absolute;
    }
  }
</style>
